import { isEmpty, get, set } from 'lodash';
import {convertUnits, invertObject} from './common';
import adminStatistics from './adminStatistics';

function getGlobalAdminStatistics(globalAdminStatsDat, globalAdminStatsJson) {
  const statistics = [];

  if (isEmpty(globalAdminStatsDat) || isEmpty(globalAdminStatsJson)) {
    return statistics;
  }

  const clients = Object.values(globalAdminStatsJson.clients);
  clients.forEach(clientName => {
    const clientNum = parseInt(invertObject(globalAdminStatsJson.clients)[clientName], 10);
    const adminStatsDat = globalAdminStatsDat.filter(row => row[0] === clientNum).map(row => row.slice(1));
    const adminStatsJson = globalAdminStatsJson[clientName];
    const adminStats = adminStatistics.getAdminStatistics(adminStatsDat, adminStatsJson);

    const sidekickTotalDiskUsage = globalAdminStatsJson[clientName].disk_usage.sidekick_total_disk_usage;
    const sidekickSvnTotalDiskUsage = globalAdminStatsJson[clientName].disk_usage.sidekick_svn_total_disk_usage;

    const clientStats = {
      'name': clientName,
      ...adminStats,
      'numberOfProjects': globalAdminStatsJson[clientName].number_of_projects,
      'workPapersDiskUsage': sidekickTotalDiskUsage - sidekickSvnTotalDiskUsage,
      'svnFolderDiskUsage': sidekickSvnTotalDiskUsage,
      'svnDbFolderDiskUsage': globalAdminStatsJson[clientName].disk_usage.svnroot_db_total_disk_usage,
      'repositoryDiskUsage': globalAdminStatsJson[clientName].disk_usage.svnroot_total_disk_usage,
      'htmlDiskUsage': globalAdminStatsJson[clientName].disk_usage.tmi_sites_total_disk_usage,
    };
    statistics.push(clientStats);
 });

  return statistics;
}

function getClientData(globalAdminStatsJson) {
  const clientData = [];

  if (isEmpty(globalAdminStatsJson)) {
    return clientData;
  }

  const clients = Object.values(globalAdminStatsJson.clients);
  clients.forEach(clientName => {
    const client = globalAdminStatsJson[clientName];
    const repoName =
      client.users_from_app.length > 0 ?
      client.users_from_app[0].username.split('@')[1] : '';
    const clientStats = {
      'name': client.name,
      'repoName': repoName || 'not_found',
      'address': client.address,
      'city': client.city,
      'email': client.email,
      'vat': client.vat,
      'countryCode': client.country_code,
      'countryName': client.country_name,
    };
    clientData.push(clientStats);
  });

  return clientData;
}

function getClientAdmins(globalAdminStatsJson) {
  const clientAdmins = [];

  if (isEmpty(globalAdminStatsJson)) {
    return clientAdmins;
  }

  const clients = Object.values(globalAdminStatsJson.clients);
  clients.forEach(clientName => {
    const clientUsers = globalAdminStatsJson[clientName].users_from_app;
    clientUsers.forEach(clientUser => {
      if (clientUser.is_client_admin) {
        const clientUserInfo = {
          'username': clientUser.username,
          'firstName': clientUser.first_name,
          'lastName': clientUser.last_name,
          'email': clientUser.email,
          'dateJoined': clientUser.date_joined,
        };
        clientAdmins.push(clientUserInfo);
      }
    })
  });

  return clientAdmins;
}

function getGlobalAdminStatisticsWithTimePeriod(clients, globalAdminStatsDat, globalAdminStatsJson, startDate, endDate) {
  const statistics = {};

  if (isEmpty(globalAdminStatsDat) || isEmpty(globalAdminStatsJson)) {
    return statistics;
  }

  clients.forEach(client => {
    const clientName = client.name;
    const clientNum = parseInt(invertObject(globalAdminStatsJson.clients)[clientName], 10);
    const adminStatsDat = globalAdminStatsDat.filter(row => row[0] === clientNum).map(row => row.slice(1));
    const adminStatsJson = globalAdminStatsJson[clientName];
    const adminStatsWithTimePeriod = adminStatistics.getAdminStatisticsWithTimePeriod(
        adminStatsDat, adminStatsJson, startDate, endDate
    );
    statistics[clientName] = adminStatsWithTimePeriod;
  });

  return statistics;
}

function getGlobalResources(globalAdminStatsJson) {
  const statistics = [];

  if (isEmpty(globalAdminStatsJson)) {
    return statistics;
  }
  
  const { disk_usage } = globalAdminStatsJson;
  
  disk_usage.forEach(filesystem => {
    const row = {
      'Filesystem': filesystem.Filesystem,
      'Mounted': filesystem.Mounted,
      'Used': convertUnits(filesystem.Used),
      'UsedPerc': filesystem['Use%'],
      'Available': convertUnits(filesystem.Available),
      'Total': convertUnits(filesystem['1B-blocks']),
    };
    statistics.push(row);
  });
  
  return statistics;
}

function getSubscriptionChangesInfo(firstSubscriptionChange, subscriptionChangesRecord) {
  const { status } = firstSubscriptionChange;
  const activatedStatuses = ['ACT'];
  const deactivatedStatuses = ['REQ', 'INA'];
  let lookForStatuses = '';
  const dates = [];

  if (activatedStatuses.includes(status)) {
    dates.push({
      startDate: firstSubscriptionChange.datetime,
    });
    lookForStatuses = 'DEACTIVATED_STATUSES';
  }
  else if (deactivatedStatuses.includes(status)) {
    lookForStatuses = 'ACTIVATED_STATUSES';
  }

  subscriptionChangesRecord.forEach(subscriptionChange => {
    const subscriptionChangeStatus = subscriptionChange.status;
    if (lookForStatuses === 'ACTIVATED_STATUSES' && activatedStatuses.includes(subscriptionChangeStatus)) {
      dates.push({
        startDate: subscriptionChange.datetime,
      });
      lookForStatuses = 'DEACTIVATED_STATUSES';
    }
    else if (lookForStatuses === 'DEACTIVATED_STATUSES' && deactivatedStatuses.includes(subscriptionChangeStatus)) {
      dates.push({
        endDate: subscriptionChange.datetime,
      });
      lookForStatuses = 'ACTIVATED_STATUSES';
    }
  });

  if (!(dates && dates.length)) {
    return [{
      'startDate': null,
      'endDate': null,
      'status': 'INA',
    }];
  }

  let lookForAttribute = 'endDate';
  const records = [{
    startDate: dates[0].startDate,
  }];

  dates.forEach(date => {
    const lookedForAttribute = get(date, lookForAttribute, false);
    if (lookedForAttribute) {
      if (lookForAttribute === 'startDate') {
        records.push({
          'startDate': lookedForAttribute,
        });
        lookForAttribute = 'endDate';
      }
      else if (lookForAttribute === 'endDate') {
        records[records.length - 1].endDate = lookedForAttribute;
        lookForAttribute = 'startDate';
      }
    }
  });

  return records;
}

function getSubscriptionInfo(subscriptionChanges, lastSubscriptions, activeSubscribers, services) {
  const statistics = {};

  if (isEmpty(activeSubscribers)) {
    return statistics;
  }

  // Names are both usernames and client names
  const lastSubscriptionsNames = Object.keys(lastSubscriptions);
  const subscriptionChangesNames = Object.keys(subscriptionChanges);

  lastSubscriptionsNames.forEach(name => {
     services.forEach(service => {
       const property = [name, service];
       const generalInfoPath = [name, 'generalInfo'];
       const saveToPath = [name, 'services', service];
       const subscriptionChangesRecord = get(subscriptionChanges, property, false);
       const lastSubscriptionsRecord = get(lastSubscriptions, property, false);
       if (!subscriptionChangesRecord && lastSubscriptionsRecord) {
        const { status } = lastSubscriptionsRecord;
        if (status) {
          set(statistics, generalInfoPath, {
            clientRepoName: lastSubscriptionsRecord.length ? lastSubscriptionsRecord[0].repo_name : lastSubscriptionsRecord.repo_name,
            userEmail: lastSubscriptionsRecord.length ? lastSubscriptionsRecord[0].user_email : lastSubscriptionsRecord.user_email,
          });
          set(statistics, saveToPath, [{
            startDate: null,
            endDate: null,
            status,
          }]);
        }
       }
       else if (lastSubscriptionsRecord) {
         set(statistics, generalInfoPath, {
           clientRepoName: lastSubscriptionsRecord.length ? lastSubscriptionsRecord[0].repo_name : lastSubscriptionsRecord.repo_name,
           userEmail: lastSubscriptionsRecord.length ? lastSubscriptionsRecord[0].user_email : lastSubscriptionsRecord.user_email,
         });
         set(statistics, saveToPath, getSubscriptionChangesInfo(
             lastSubscriptionsRecord,
             subscriptionChangesRecord)
         );
       }
     })
   });

  subscriptionChangesNames.forEach(name => {
    services.forEach(service => {
      const property = [name, service];
      const generalInfoPath = [name, 'generalInfo'];
      const saveToPath = [name, 'services', service];
      const subscriptionChangesRecord = get(subscriptionChanges, property, false);
      const lastSubscriptionsRecord = get(lastSubscriptions, property, false);
      if (!lastSubscriptionsRecord && subscriptionChangesRecord) {
        set(statistics, generalInfoPath, {
          clientRepoName: subscriptionChangesRecord.length ? subscriptionChangesRecord[0].repo_name : subscriptionChangesRecord.repo_name,
          userEmail: subscriptionChangesRecord.length ? subscriptionChangesRecord[0].user_email : subscriptionChangesRecord.user_email,
        });
        set(statistics, saveToPath, getSubscriptionChangesInfo(
            subscriptionChangesRecord[0],
            subscriptionChangesRecord,
        ));
      }
    })
  });

  return statistics;
}

export default {
  getGlobalAdminStatistics,
  getClientData,
  getClientAdmins,
  getGlobalAdminStatisticsWithTimePeriod,
  getGlobalResources,
  getSubscriptionInfo,
};
